import axios from "axios";

const API_URL = "/api";

const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// REQUEST interceptor — attach token everywhere Apache can't strip it
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("coachylb_token");
  if (token) {
    // Primary: Authorization header (works for GET, may be stripped on POST by Hostinger)
    config.headers.Authorization = `Bearer ${token}`;
    // Secondary: custom header (Apache passes these through always)
    config.headers["X-Token"] = token;
    // Ultimate fallback: inject token into POST/PUT/PATCH body
    // Auth middleware reads req.body._token if headers are stripped
    const method = (config.method || "").toLowerCase();
    if (method === "post" || method === "put" || method === "patch") {
      if (config.data) {
        try {
          const body =
            typeof config.data === "string"
              ? JSON.parse(config.data)
              : config.data;
          body._token = token;
          config.data = JSON.stringify(body);
        } catch (e) {
          // if body is not JSON, skip body injection
        }
      } else {
        config.data = JSON.stringify({ _token: token });
      }
    }
  }
  return config;
});

// RESPONSE interceptor — only auto-logout on GET 401s, never on mutations
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const method = (err.config?.method || "").toLowerCase();
    const isMutation =
      method === "post" ||
      method === "put" ||
      method === "patch" ||
      method === "delete";

    if (status === 401 && !isMutation) {
      localStorage.removeItem("coachylb_token");
      localStorage.removeItem("coachylb_user");
      if (window.location.pathname.startsWith("/dashboard"))
        window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

// ============ AUTH ============
export const authAPI = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  validateCoach: (coach_email) =>
    api.post("/auth/validate-coach", { coach_email }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    api.post("/auth/reset-password", { token, password }),
  getProfile: () => api.get("/auth/profile"),
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.put("/auth/change-password", data),
};

// ============ USERS / CLIENTS ============
export const usersAPI = {
  getAllUsers: (params) => api.get("/users", { params }),
  getClients: (params) => api.get("/users/clients", { params }),
  getClient: (id) => api.get(`/users/clients/${id}`),
  deactivateUser: (id) => api.patch(`/users/${id}/deactivate`),
  reactivateUser: (id) => api.patch(`/users/${id}/reactivate`),
};

// ============ CLIENT PLANS ============
export const clientPlansAPI = {
  getActive: (client_id) =>
    api.get("/client-plans/active", { params: { client_id } }),
  getById: (id) => api.get(`/client-plans/${id}`),
  getHistory: (client_id, params) =>
    api.get("/client-plans/history", { params: { client_id, ...params } }),
  getCoachPlans: (params) => api.get("/client-plans/coach", { params }),
  create: (data) => api.post("/client-plans", data),
  assignTemplate: (data) => api.post("/client-plans/assign-template", data),
  deletePlan: (id) => api.delete(`/client-plans/${id}`),
  renew: (id) => api.post(`/client-plans/${id}/renew`),
  editAndRenew: (id, data) => api.post(`/client-plans/${id}/edit-renew`, data),
  updatePlan: (id, data) => api.put(`/client-plans/${id}`, data),
  updateExerciseFeedback: (exerciseId, data) =>
    api.patch(`/client-plans/exercises/${exerciseId}/feedback`, data),
};

// ============ LIBRARY ============
export const libraryAPI = {
  getExercises: () => api.get("/library/exercises"),
  createExercise: (data) => api.post("/library/exercises", data),
  updateExercise: (id, data) => api.put(`/library/exercises/${id}`, data),
  deleteExercise: (id) => api.delete(`/library/exercises/${id}`),
  copyExercise: (id) => api.post(`/library/exercises/${id}/copy`),
  getFoods: () => api.get("/library/foods"),
  createFood: (data) => api.post("/library/foods", data),
  updateFood: (id, data) => api.put(`/library/foods/${id}`, data),
  deleteFood: (id) => api.delete(`/library/foods/${id}`),
  copyFood: (id) => api.post(`/library/foods/${id}/copy`),
};

// ============ PLANS ============
export const plansAPI = {
  getAll: (params) => api.get("/plans", { params }),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post("/plans", data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  remove: (id) => api.delete(`/plans/${id}`),
};

// ============ SUBSCRIPTIONS ============
export const subscriptionsAPI = {
  getAll: (params) => api.get("/subscriptions", { params }),
  getMine: () => api.get("/subscriptions/mine"),
  getByCoach: (coachId) => api.get(`/subscriptions/coach/${coachId}`),
  createForCoach: (data) => api.post("/subscriptions", data),
  deleteSub: (id) => api.delete(`/subscriptions/${id}`),
  renewSub: (id) => api.post(`/subscriptions/${id}/renew`),
  updateMaxClients: (id, max_clients) =>
    api.patch(`/subscriptions/${id}/max-clients`, { max_clients }),
  updateStatus: (id, status) =>
    api.patch(`/subscriptions/${id}/status`, { status }),
};

// ============ PLAN REQUESTS ============
export const planRequestsAPI = {
  getAll: (params) => api.get("/plan-requests", { params }),
  create: (data) => api.post("/plan-requests", data),
  updateStatus: (id, data) => api.patch(`/plan-requests/${id}`, data),
  remove: (id) => api.delete(`/plan-requests/${id}`),
};

// ============ PAYMENTS ============
export const paymentsAPI = {
  getAll: (params) => api.get("/payments", { params }),
  getMine: () => api.get("/payments/mine"),
  create: (plan_id) => api.post("/payments/create", { plan_id }),
  selfConfirm: (transaction_id) =>
    api.post("/payments/self-confirm", { transaction_id }),
  confirm: (transaction_id) =>
    api.post("/payments/confirm", { transaction_id }),
  resetRevenue: () => api.post("/payments/reset-revenue"),
  getStats: () => api.get("/payments/stats"),
  initiateWhish: (plan_id, currency = "USD") =>
    api.post("/payments/whish-initiate", { plan_id, currency }),
};

// ============ WORKOUTS ============
export const workoutsAPI = {
  getAll: () => api.get("/workouts"),
  getById: (id) => api.get(`/workouts/${id}`),
  create: (data) => api.post("/workouts", data),
  update: (id, data) => api.put(`/workouts/${id}`, data),
  remove: (id) => api.delete(`/workouts/${id}`),
  assign: (client_id, workout_id) =>
    api.post("/workouts/assign", { client_id, workout_id }),
};

// ============ DIETS ============
export const dietsAPI = {
  getAll: () => api.get("/diets"),
  getById: (id) => api.get(`/diets/${id}`),
  create: (data) => api.post("/diets", data),
  update: (id, data) => api.put(`/diets/${id}`, data),
  remove: (id) => api.delete(`/diets/${id}`),
  assign: (client_id, diet_id) =>
    api.post("/diets/assign", { client_id, diet_id }),
};

// ============ TRACKER ============
export const trackerAPI = {
  getEntries: (params) => api.get("/tracker", { params }),
  upsert: (data) => api.post("/tracker", data),
  getLatest: (client_id) =>
    api.get("/tracker/latest", { params: { client_id } }),
  getClientStats: () => api.get("/tracker/client-stats"),
  getTrends: (client_id, field, limit) =>
    api.get("/tracker/trends", { params: { client_id, field, limit } }),
};

// ============ STATS ============
export const statsAPI = {
  getAdmin: () => api.get("/stats/admin"),
  getCoach: () => api.get("/stats/coach"),
};

// ============ TUTORIALS ============
export const tutorialsAPI = {
  getExercises: (search = "") =>
    api.get("/tutorials/exercises", { params: { search } }),
  saveExercise: (name, video_url) =>
    api.post("/tutorials/exercises", { name, video_url }),
  deleteExercise: (id) => api.delete(`/tutorials/exercises/${id}`),
  getWebsite: () => api.get("/tutorials/website"),
  updateWebsite: (key, video_url) =>
    api.put(`/tutorials/website/${key}`, { video_url }),
  getLandingVideo: () => api.get("/tutorials/website/public/landing"),
};

export default api;
